import axios from './axios';
import { Getter } from './batch';
import * as models from './models';
import * as phone from './phone';

export declare interface Country {
    id: number;
    name: string;
    code2: string;
    code3: string;
    states?: State[];
}

export declare interface State {
    id: number;
    name: string;
    code: string;
    apo?: boolean;
}

declare interface getCountriesResp {
    countries: models.Country[];
}

let countries: Promise<models.Country[]>;
const countriesMap: models.Country[] = [];
const statesMap: models.State[][] = [];

export async function GetCountries(getter: Getter = axios): Promise<models.Country[]> {
    if (typeof countries !== 'undefined') {
        return countries;
    }

    countries = getter.get<getCountriesResp>('/countries', {
        params: {
            'version': '2023-04-13',
        },
    }).then(resp => resp.data.countries);

    for (const c of await countries) {
        if (!c.id) {
            throw new Error('country is missing id');
        }

        countriesMap[c.id] = c;

        if (c.states) {
            for (const s of c.states) {
                if (!s.id) {
                    throw new Error('state is missing id');
                }

                if (!statesMap[c.id]) {
                    statesMap[c.id] = [];
                }

                statesMap[c.id][s.id] = s;
            }
        }
    }

    return countries;
}

export async function GetCountry(id?: number, getter: Getter = axios): Promise<models.Country | undefined> {
    await GetCountries(getter);

    if (!id) {
        return;
    }
    return countriesMap[id];
}

export async function GetState(countryID?: number, stateID?: number, getter: Getter = axios): Promise<models.State | undefined> {
    await GetCountries(getter);

    if (!countryID || !stateID) {
        return;
    }
    return statesMap[countryID]?.[stateID];
}

export async function GetCountryAndState(countryID?: number, stateID?: number, getter: Getter = axios): Promise<{ country: models.Country | undefined; state: models.State | undefined }> {
    await GetCountries(getter);

    return {
        country: countryID ? countriesMap[countryID] : undefined,
        state: countryID && stateID ? statesMap[countryID]?.[stateID] : undefined,
    };
}

// GetCountrySync should only be called if you *know* the countries have already been loaded
export function GetCountrySync(id: number): models.Country | undefined {
    return countriesMap[id];
}

export function GetStateSync(countryID: number, stateID: number): models.State | undefined {
    return statesMap[countryID]?.[stateID];
}

interface selectsOpts {
    defaultUnitedStates?: boolean;
    stateParentSelector?: string;
    shortStates?: boolean;
    phone?: phone.Phone;
    onStateHide?: () => void;
    onStateShow?: () => void;
    getter?: Getter;
}

export async function Selects(countrySelect: HTMLSelectElement, stateSelect?: HTMLSelectElement, opts: selectsOpts = {}) {
    opts.stateParentSelector ??= '.form-group,.form-row,.row';

    const countries = await GetCountries(opts.getter);

    const options = document.createDocumentFragment();
    for (const c of countries) {
        const newOption = document.createElement('option');
        newOption.value = String(c.id);
        newOption.textContent = c.name ?? '';
        newOption.setAttribute('data-country-code-2', c.countryCode2 ?? '');
        newOption.setAttribute('data-country-code-3', c.countryCode3 ?? '');
        newOption.setAttribute('data-name', c.name ?? '');

        options.appendChild(newOption);
    }

    countrySelect.appendChild(options);

    if (stateSelect) {
        const startingStates = stateSelect.innerHTML;

        countrySelect.addEventListener('change', () => {
            if (!countrySelect.value) {
                return;
            }

            const country = GetCountrySync(parseInt(countrySelect.value, 10));
            if (!country) {
                return;
            }

            const states: models.State[] | undefined = country?.states;

            if (opts.phone && country?.countryCode2) {
                try {
                    opts.phone.Iti.setCountry(country.countryCode2);
                } catch (e) {
                    console.warn('Phone not initialized', e);
                }
            }

            if (!opts.stateParentSelector) {
                throw new Error('stateParentSelector is required');
            }

            if (!states?.length) {
                if (opts?.onStateHide) {
                    opts.onStateHide();
                }

                stateSelect.closest(opts.stateParentSelector)?.classList.add('d-none');
                stateSelect.disabled = true;
                return;
            }

            if (opts?.onStateShow) {
                opts.onStateShow();
            }

            stateSelect.innerHTML = startingStates;

            const options = document.createDocumentFragment();
            for (const s of states) {
                const newOption = document.createElement('option');
                newOption.value = String(s.id);
                newOption.setAttribute('data-state-code', s.stateCode ?? '');
                newOption.setAttribute('data-name', s.name ?? '');

                if (opts.shortStates) {
                    newOption.textContent = s.stateCode ?? '';
                    newOption.title = s.name ?? '';
                } else {
                    newOption.textContent = s.name ?? '';
                }

                options.appendChild(newOption);
            }

            stateSelect.appendChild(options);

            stateSelect.closest(opts.stateParentSelector)?.classList.remove('d-none');
            stateSelect.disabled = false;
        });
    }

    if (opts?.defaultUnitedStates) {
        countrySelect.value = String(models.CountryID.COUNTRIES_UNITED_STATES);
        countrySelect.dispatchEvent(new Event('change'));
    }
}

export async function StateSelect(countryID: number, stateSelect: HTMLSelectElement, opts: selectsOpts = {}, startingStates?: string) {
    opts.stateParentSelector ??= '.form-group,.form-row,.row';

    await GetCountries(opts.getter).then(() => {
        const country = GetCountrySync(countryID);
        if (!country) {
            return;
        }

        if (!startingStates) {
            startingStates = stateSelect.innerHTML;
        }

        const states: models.State[] | undefined = country?.states;

        if (opts.phone && country?.countryCode2) {
            opts.phone.Iti.setCountry(country.countryCode2);
        }

        if (!opts.stateParentSelector) {
            throw new Error('stateParentSelector is required');
        }

        if (!states?.length) {
            if (opts?.onStateHide) {
                opts.onStateHide();
            }

            stateSelect.closest(opts.stateParentSelector)?.classList.add('d-none');
            stateSelect.disabled = true;
            return;
        }

        if (opts?.onStateShow) {
            opts.onStateShow();
        }

        stateSelect.innerHTML = startingStates;

        const options = document.createDocumentFragment();
        for (const s of states) {
            const newOption = document.createElement('option');
            newOption.value = String(s.id);
            newOption.setAttribute('data-state-code', s.stateCode ?? '');
            newOption.setAttribute('data-name', s.name ?? '');

            if (opts.shortStates) {
                newOption.textContent = s.stateCode ?? '';
                newOption.title = s.name ?? '';
            } else {
                newOption.textContent = s.name ?? '';
            }

            options.appendChild(newOption);
        }

        stateSelect.appendChild(options);

        stateSelect.closest(opts.stateParentSelector)?.classList.remove('d-none');
        stateSelect.disabled = false;
    });
}

export interface CountryAndStater {
    countryID?: models.CountryID;
    country?: models.Country;
    stateID?: models.StateID;
    state?: models.State;
}

export async function UpdateCountryAndState(obj: CountryAndStater) {
    if (!obj.country?.countryCode2) {
        if (obj.countryID ?? obj.country?.id) {
            obj.country = await GetCountry(obj.countryID ?? obj.country?.id);
        } else if (obj.country?.countryCode3) {
            obj.country = await GetCountryByCode3(obj.country.countryCode3);
        } else if (obj.country?.name) {
            obj.country = await GetCountryByName(obj.country.name);
        }
    }

    if (!obj.country?.countryCode3) {
        if (obj.countryID ?? obj.country?.id) {
            obj.country = await GetCountry(obj.countryID ?? obj.country?.id);
        } else if (obj.country?.countryCode2) {
            obj.country = await GetCountryByCode2(obj.country.countryCode2);
        } else if (obj.country?.name) {
            obj.country = await GetCountryByName(obj.country.name);
        }
    }

    if (!obj.country?.name) {
        if (obj.countryID ?? obj.country?.id) {
            obj.country = await GetCountry(obj.countryID ?? obj.country?.id);
        } else if (obj.country?.countryCode2) {
            obj.country = await GetCountryByCode2(obj.country.countryCode2);
        } else if (obj.country?.countryCode3) {
            obj.country = await GetCountryByCode3(obj.country.countryCode3);
        }
    }

    if (!(obj.countryID ?? obj.country?.id)) {
        if (obj.country?.countryCode2) {
            obj.country = await GetCountryByCode2(obj.country.countryCode2);
        } else if (obj.country?.countryCode3) {
            obj.country = await GetCountryByCode3(obj.country.countryCode3);
        } else if (obj.country?.name) {
            obj.country = await GetCountryByName(obj.country.name);
        }
    }

    obj.countryID = obj.country?.id;

    if (obj.countryID) {
        if (!obj.state?.stateCode) {
            if (obj.stateID ?? obj.state?.id) {
                obj.state = await GetState(obj.countryID, obj.stateID ?? obj.state?.id);
            } else if (obj.state?.name) {
                obj.state = await GetStateByName(obj.countryID, obj.state.name);
            }
        }

        if (!obj.state?.name) {
            if (obj.stateID ?? obj.state?.id) {
                obj.state = await GetState(obj.countryID, obj.stateID ?? obj.state?.id);
            } else if (obj.state?.stateCode) {
                obj.state = await GetStateByCode(obj.countryID, obj.state.stateCode);
            }
        }

        if (!(obj.stateID ?? obj.state?.id)) {
            if (obj.state?.stateCode) {
                obj.state = await GetStateByCode(obj.countryID, obj.state.stateCode);
            } else if (obj.state?.name) {
                obj.state = await GetStateByName(obj.countryID, obj.state.name);
            }
        }

        obj.stateID = obj.state?.id;
    }
}

export async function GetCountryByCode2(countryCode2: string, getter: Getter = axios): Promise<models.Country | undefined> {
    await GetCountries(getter);
    for (const country of countriesMap.values()) {
        if (!country) {
            continue;
        }
        if (country.countryCode2?.localeCompare(countryCode2, undefined, { sensitivity: 'base' }) === 0) {
            return country;
        }
    }
}

export async function GetCountryByCode3(countryCode3: string, getter: Getter = axios): Promise<models.Country | undefined> {
    await GetCountries(getter);
    for (const country of countriesMap.values()) {
        if (!country) {
            continue;
        }
        if (country.countryCode3?.localeCompare(countryCode3, undefined, { sensitivity: 'base' }) === 0) {
            return country;
        }
    }
}

export async function GetCountryByName(name: string, getter: Getter = axios): Promise<models.Country | undefined> {
    await GetCountries(getter);
    for (const country of countriesMap.values()) {
        if (!country) {
            continue;
        }
        if (country.name?.localeCompare(name, undefined, { sensitivity: 'base' }) === 0) {
            return country;
        }
    }
}

export async function GetCountryFromPhone(p: phone.Phone): Promise<models.Country | undefined> {
    const countryData = p.Iti.getSelectedCountryData();
    return await GetCountryByCode2(countryData?.iso2 ?? '');
}

export async function GetCountryIDFromPhone(p: phone.Phone): Promise<number | undefined> {
    const country = await GetCountryFromPhone(p);
    return country?.id;
}

export async function GetStateByCode(countryID: number, stateCode: string, getter: Getter = axios): Promise<models.State | undefined> {
    await GetCountries(getter);
    for (const state of statesMap[countryID]?.values() ?? []) {
        if (!state) {
            continue;
        }
        if (state.stateCode?.localeCompare(stateCode, undefined, { sensitivity: 'base' }) === 0) {
            return state;
        }
    }
}

export async function GetStateByName(countryID: number, name: string, getter: Getter = axios): Promise<models.State | undefined> {
    await GetCountries(getter);
    for (const state of statesMap[countryID]?.values() ?? []) {
        if (!state) {
            continue;
        }
        if (state.name?.localeCompare(name, undefined, { sensitivity: 'base' }) === 0) {
            return state;
        }
    }
}
